import React, { useState } from 'react';
import './settings.scss';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import PresetDTO from '../../../dto/PresetDTO';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import CheckBoxInputWithLabel from '../../../components/fields/CheckBoxInput/CheckBoxInputWithLabel';
import PresetsSettingsModal from './PresetsSettingsModal';
import { getSettingsData } from '../reducer';
import { createPreset, savePresets } from '../actions';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { getResponseError } from '../../../utils/utils';
import PresetDayDTO from '../../../dto/PresetDayDTO';
import TextInput from '../../../components/fields/TextInput/TextInput';
import TimePicker from '../../../components/fields/TimePicker/TimePicker';

export default function PresetsForm(props) {
  const { data = {}, cancel, loadData = () => {} } = props;
  const intl = useIntl();
  const { formatMessage } = intl;
  const { id } = data;
  const [showCollage, setShowCollage] = useState(false);
  const DTO = new PresetDTO();
  const dayDTO = new PresetDayDTO();
  const attributes = DTO.getAttributesByName();
  const dayAttributes = dayDTO.getAttributesByName();
  const dispatch = useDispatch();
  const { violationList = [] } = useSelector(getSettingsData);
  const {
    control,
    setValue,
    getValues,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      days: {
        all: false, even: false, odd: false, working: false, holidays: false, saturday: false, sunday: false,
      },
      save_intermediate: false,
      show_intermediate: false,
      ...data,
    },
    resetOptions: {
      keepDirtyValues: true, // user-interacted input will be retained
      keepErrors: true, // input errors will be retained with value update
    },
  });
  const save = () => {
    if (!id) {
      dispatch(createPreset(getValues())).then(() => {
        loadData();
        cancel();
        successNotification(formatMessage({ id: 'preset.create' }), formatMessage({ id: 'preset.create.success' }));
      }).catch(response => {
        errorNotification(formatMessage({ id: 'preset.create' }), getResponseError(response));
      });
    } else {
      dispatch(savePresets({
        id,
        data: getValues(),
      })).then(() => {
        loadData();
        cancel();
        successNotification(formatMessage({ id: 'preset.save' }), formatMessage({ id: 'preset.save.success' }));
      }).catch(response => {
        errorNotification(formatMessage({ id: 'preset.save' }), getResponseError(response));
      });
    }
  };
  const intervals = [{ value: 15, name: `15 ${formatMessage({ id: 'minute.short' })}` }, { value: 30, name: `30 ${formatMessage({ id: 'minute.short' })}` }];
  return (
    <>
      <div className="settings-form">
        <div className="settings-view-form preset-option">
          <FieldDTO
            control={control}
            component={TextInputWithLabel}
            dtoObject={attributes.name}
            variant="material"
          />
          <div className="settings-view-row">
            <FieldDTO
              control={control}
              component={TimePicker}
              dtoObject={attributes.start}
              variant="material"
              hideSeconds
              inputAfter={<Icons customIcon width={20} height={20} icon="clock" />}
            />
            <FieldDTO
              control={control}
              component={TimePicker}
              dtoObject={attributes.end}
              variant="material"
              hideSeconds
              inputAfter={<Icons customIcon width={20} height={20} icon="clock" />}
            />
          </div>
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.interval}
            variant="material"
            options={intervals}
            withLabel
            className="mt-3"
          />
          <FieldDTO
            control={control}
            component={CheckBoxInputWithLabel}
            dtoObject={attributes.save_intermediate}
            variant="material"
            className="mt-3"
          />
          <FieldDTO
            control={control}
            component={CheckBoxInputWithLabel}
            dtoObject={attributes.show_intermediate}
            variant="material"
            className="mt-3"
          />
          <div className="hr-row mt-3" />
          <div className="f4 mt-3 mb-3">{formatMessage({ id: 'select.days' })}</div>
          <table className="days-table">
            <tbody>
              <tr>
                <td>
                  <FieldDTO
                    control={control}
                    component={CheckBoxInputWithLabel}
                    dtoObject={dayAttributes['days.all']}
                    variant="material"
                  />
                </td>
                <td>
                  <FieldDTO
                    control={control}
                    component={CheckBoxInputWithLabel}
                    dtoObject={dayAttributes['days.even']}
                    variant="material"
                  />
                </td>
                <td>
                  <FieldDTO
                    control={control}
                    component={CheckBoxInputWithLabel}
                    dtoObject={dayAttributes['days.working']}
                    variant="material"
                  />
                </td>
                <td>
                  <FieldDTO
                    control={control}
                    component={CheckBoxInputWithLabel}
                    dtoObject={dayAttributes['days.saturday']}
                    variant="material"
                  />
                </td>
              </tr>
              <tr>
                <td />
                <td>
                  <FieldDTO
                    control={control}
                    component={CheckBoxInputWithLabel}
                    dtoObject={dayAttributes['days.odd']}
                    variant="material"
                    className="mt-3"
                  />
                </td>
                <td>
                  <FieldDTO
                    control={control}
                    component={CheckBoxInputWithLabel}
                    dtoObject={dayAttributes['days.holidays']}
                    variant="material"
                    className="mt-3"
                    disabled
                  />
                </td>
                <td>
                  <FieldDTO
                    control={control}
                    component={CheckBoxInputWithLabel}
                    dtoObject={dayAttributes['days.sunday']}
                    variant="material"
                    className="mt-3"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="hr-row mt-3" />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.violation_type_id}
            variant="material"
            options={violationList.map(v => ({ value: v.id, name: v.code_description }))}
            withLabel
            className="mt-3"
          />
          <div className="hr-row mt-3" />
          <div className="f4 mt-3 mb-3">{formatMessage({ id: 'collage.settings' })}</div>
          <FieldDTO
            control={control}
            component={TextInput}
            type="hidden"
            dtoObject={attributes.collage_settings}
          />
          <Button className="collage-settings-btn" variant="secondary" onClick={() => { setShowCollage(true); }}>
            <Icons width={24} height={24} customIcon icon="gear" className="button-icons" />
            {formatMessage({ id: 'tune' })}
          </Button>
          {showCollage
            ? (
              <PresetsSettingsModal
                handleClose={() => setShowCollage(false)}
                handleSave={value => { setValue('collage_settings', value); setShowCollage(false); }}
                data={data}
              />
            ) : null}
        </div>
      </div>
      <div className="settings-footer">
        <Button className="clear-show" onClick={save}>{formatMessage({ id: 'app.save' })}</Button>
        <Button className="clear-data" variant="secondary" onClick={cancel}>{formatMessage({ id: 'app.cancel' })}</Button>
      </div>
    </>
  );
}
